import { isEmpty } from 'lodash';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Icon, Dialog, Typography, Skeleton } from '@sravni/react-design-system';
import { TailArrowRight, Cross } from '@sravni/react-icons';
import { useBoolean, useIsMobile, usePrevious, useAnalytics, useStyles } from '@sravni/react-utils';
import InputSource from '../../constants/inputSource.js';
import { MAX_QUERY_LENGTH } from '../../constants/limits.js';
import { URLS } from '../../constants/urls.js';
import { MESSAGE_TYPE } from '../../enums/MessageTypes.js';
import SearchSuggestsStatus from '../../enums/SearchSuggestsStatus.js';
import { useMobileForm } from '../../hooks/useMobileForm.js';
import { useDebounceCallback } from '../../hooks/useDebounceCallback.js';
import { useDevice } from '../../hooks/useDevice.js';
import { addParams } from '../../utils/addParams.js';
import { createMessage } from '../../utils/createMessage.js';
import { SearchLogger } from '../../utils/searchLogger.js';
import { ErrorResponse } from '../ErrorResponse/ErrorResponse.js';
import SuggestList from '../SuggestList/index.js';
import { GoogleResultList } from './GoogleResultList.js';
import { Scenario } from './Scenario.js';
import modules_1ba53a2f from './styles/GlobalSearch.module.less.js';
import { TextInput } from './TextInput.js';

const { Text } = Typography;
const DEFAULT_SUGGESTS = [
    {
        id: '669917cd9f39fe0026a93672',
        searchRequest: 'Осаго',
        url: 'https://www.sravni.ru/osago/',
        scenario: 'search-osago-v2',
        weight: 2291,
        position: 3,
        _score: 0,
    },
    {
        id: '669917cd9f39fe0026a9366f',
        searchRequest: 'Курсы валют',
        url: 'https://www.sravni.ru/valjuty/',
        weight: 1442,
        position: 4,
        _score: 0,
    },
    {
        id: '669917cd9f39fe0026a93680',
        searchRequest: 'Каско',
        url: 'https://www.sravni.ru/kasko/',
        weight: 1079,
        position: 21,
        _score: 0,
    },
    {
        id: '669917cd9f39fe0026a9366d',
        searchRequest: 'Потребительские кредиты',
        url: 'https://www.sravni.ru/kredity/',
        weight: 1065,
        position: 2,
        _score: 0,
    },
    {
        id: '669917cd9f39fe0026a9366c',
        searchRequest: 'Вклады',
        url: 'https://www.sravni.ru/vklady/',
        weight: 1048,
        position: 1,
        _score: 0,
    },
];
// eslint-disable-next-line max-statements,complexity
const SearchDialog = ({ isVisible, onClose, searchServiceHost, asPath }) => {
    const [status, setStatus] = useState(SearchSuggestsStatus.Default);
    const [currentResultsPage, setCurrentResultsPage] = useState(0);
    const [resultsTitle, setResultsTitle] = useState('');
    const [isResultsDirty, setIsResultsDirty] = useBoolean(false);
    const [suggests, setSuggests] = useState(DEFAULT_SUGGESTS);
    const [focusedSuggestIndex, setFocusedSuggestIndex] = useState(-1);
    const [query, setQuery] = useState('');
    const [pasted, setPasted] = useState([]);
    const [eventSent, setEventSent] = useState(null);
    const [scenarioData, setScenarioData] = useState(null);
    const [scenarioQuery, setScenarioQuery] = useState('');
    const isMobile = useIsMobile();
    const serviceUrl = useMemo(() => new URL(URLS.SEARCH_REQUEST, searchServiceHost), [searchServiceHost]);
    const serviceLoggerUrl = useMemo(() => new URL(URLS.SEARCH_LOGGER, searchServiceHost), [searchServiceHost]);
    const suggestRequested = useRef(false);
    const prevQuery = usePrevious(query);
    const device = useDevice();
    const { pushEvent } = useAnalytics();
    const cx = useStyles(modules_1ba53a2f);
    const searchLogger = useMemo(() => new SearchLogger(serviceLoggerUrl, device), [serviceLoggerUrl, device]);
    const dialogContentRef = useRef(null);
    const [isResultsMode, setIsResultsMode] = useBoolean(false);
    const [isScenarioMode, setIsScenarioMode] = useBoolean(false);
    const [resultItems, setResultItems] = useState([]);
    const [isResultsLoading, setIsResultsLoading] = useBoolean(false);
    const [resultSuggests, setResultSuggest] = useState([]);
    const [resultQuery, setResultQuery] = useState('');
    const resultSuggestRequested = useRef(false);
    const prevResultQuery = usePrevious(resultQuery);
    const inputRef = useRef(null);
    const contentMainRef = useRef(null);
    const isResultsFocused = useMemo(() => !!resultQuery, [resultQuery]);
    useMobileForm(dialogContentRef);
    const loadSuggest = useDebounceCallback(async () => {
        if (prevQuery !== query || !suggestRequested.current) {
            suggestRequested.current = true;
            setStatus(SearchSuggestsStatus.Loading);
            try {
                // TODO: Нужно вынести уровень общения по сети в отдельный слой(создать класс для работы с данными который под капотом спрячет способ получения данных)
                const response = await fetch(`${serviceUrl}?query=${encodeURIComponent(query)}`, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Content-Type': 'application/json',
                    },
                });
                if (!response.ok)
                    throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                setStatus(SearchSuggestsStatus.Default);
                setSuggests(data.items);
            }
            catch {
                setStatus(SearchSuggestsStatus.Error);
            }
        }
    }, 300);
    const loadResultSuggest = useDebounceCallback(async () => {
        try {
            if (prevResultQuery !== resultQuery || !resultSuggestRequested.current) {
                resultSuggestRequested.current = true;
                setStatus(SearchSuggestsStatus.Loading);
                const urlWithQuery = `${serviceUrl}?query=${encodeURIComponent(resultQuery)}`;
                // TODO: Нужно вынести уровень общения по сети в отдельный слой(создать класс для работы с данными который под капотом спрячет способ получения данных)
                const data = await fetch(urlWithQuery, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Content-Type': 'application/json',
                    },
                }).then((response) => response.json());
                setStatus(SearchSuggestsStatus.Default);
                setResultSuggest(data.items);
            }
        }
        catch {
            setStatus(SearchSuggestsStatus.Error);
        }
    }, 300);
    useEffect(() => {
        loadSuggest();
    }, [query, loadSuggest]);
    useEffect(() => {
        loadResultSuggest();
    }, [resultQuery, loadResultSuggest]);
    useEffect(() => {
        if (!isVisible) {
            setQuery('');
            setResultQuery('');
            setIsResultsMode.off();
            setIsResultsDirty.off();
            setResultItems([]);
            setIsScenarioMode.off();
        }
    }, [isVisible, setIsResultsMode, setIsResultsDirty, setIsScenarioMode]);
    useEffect(() => {
        if (isResultsDirty) {
            contentMainRef.current.scrollTop = -1000;
        }
    }, [isResultsDirty]);
    const handleChange = useCallback((event) => {
        const newQuery = event.currentTarget.value;
        if (!isResultsMode) {
            setQuery(newQuery);
            setFocusedSuggestIndex(-1);
            if (!eventSent && newQuery.length === 1) {
                pushEvent({
                    event: 'mainEvent',
                    eventCategory: 'Поиск',
                    eventAction: 'Печать запроса|Тип ввода - Ручной',
                    eventLabel: 'undefined',
                });
                setEventSent(InputSource.ManualInput);
            }
        }
        else {
            setResultQuery(newQuery);
        }
    }, [eventSent, pushEvent, isResultsMode]);
    const handlePaste = useCallback((event) => {
        const pastedText = event.clipboardData.getData('text');
        setPasted((prevState) => [...prevState, pastedText]);
        if (!eventSent) {
            pushEvent({
                event: 'mainEvent',
                eventCategory: 'Поиск',
                eventAction: 'Печать запроса|Тип ввода - Буфер обмена',
                eventLabel: `undefined`,
            });
            setEventSent(InputSource.ClipboardPaste);
        }
    }, [eventSent, pushEvent]);
    const loadResults = useCallback(async () => {
        try {
            const resultsUrl = new URL(URLS.SEARCH_RESULTS, searchServiceHost);
            const searchQuery = isResultsMode && resultQuery ? resultQuery : query;
            setResultsTitle(searchQuery);
            setIsResultsMode.on();
            setIsResultsLoading.on();
            const urlWithQuery = `${resultsUrl}?query=${encodeURIComponent(searchQuery)}&page=${currentResultsPage + 1}`;
            setStatus(SearchSuggestsStatus.Loading);
            // TODO: Нужно вынести уровень общения по сети в отдельный слой(создать класс для работы с данными который под капотом спрячет способ получения данных)
            const data = await fetch(urlWithQuery, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': 'application/json',
                },
            }).then((response) => response.json());
            setStatus(SearchSuggestsStatus.Default);
            setCurrentResultsPage(currentResultsPage + 1);
            if (Array.isArray(data.items)) {
                setResultItems((prev) => [...prev, ...data.items]);
            }
            setResultQuery('');
            setIsResultsDirty.on();
            setIsResultsLoading.off();
        }
        catch {
            setStatus(SearchSuggestsStatus.Error);
        }
    }, [
        query,
        currentResultsPage,
        setIsResultsDirty,
        setIsResultsLoading,
        setIsResultsMode,
        searchServiceHost,
        setResultsTitle,
        isResultsMode,
        resultQuery,
    ]);
    const handleClick = useCallback(async () => {
        if (!(query.length > 0 && query.length <= MAX_QUERY_LENGTH))
            return;
        const currentUrl = window?.location?.origin;
        const redirectUrl = new URL(`/poisk/`, currentUrl);
        const searchParams = new URLSearchParams();
        searchParams.set('int_source', 'search');
        searchParams.set('int_medium', 'search');
        searchParams.set('int_term', query);
        searchParams.set('q', query);
        searchParams.set('int_campaign', 'classical');
        searchParams.set('int_content', window?.location?.pathname);
        redirectUrl.search = searchParams.toString();
        await searchLogger.log({
            query,
            requestType: 'search',
            redirectUrl: redirectUrl.toString(),
            asPath,
            suggestsCount: suggests.length,
            pasted,
        });
        setPasted([]);
        pushEvent({
            event: 'mainEvent',
            eventCategory: 'Поиск',
            eventAction: 'Отправка запроса|Тип выдачи - Классическая',
            eventLabel: `Запрос - ${query}|Способ ввода - Enter`,
        });
        if (!isEmpty(resultSuggests)) {
            setSuggests(() => [...resultSuggests]);
        }
        setResultItems([]);
        await loadResults();
        inputRef.current?.blur();
    }, [query, searchLogger, asPath, suggests.length, pasted, pushEvent, resultSuggests, loadResults]);
    const handleSuggestClick = useCallback(async (url, label, scenarioName) => {
        if (scenarioName) {
            const startUrl = new URL(URLS.FLOW_START, searchServiceHost);
            setStatus(SearchSuggestsStatus.Loading);
            // TODO: Нужно вынести уровень общения по сети в отдельный слой(создать класс для работы с данными который под капотом спрячет способ получения данных)
            const data = await fetch(startUrl, {
                method: 'POST',
                body: JSON.stringify({
                    scenarioName,
                    owner: '',
                    platform: '',
                    appVersion: '',
                }),
                headers: {
                    Accept: 'application/json',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': 'application/json',
                },
            }).then((response) => response.json());
            setScenarioData({ ...data.response, scenarioName: scenarioName.split('/')[0] });
            setScenarioQuery(label);
            setIsScenarioMode.on();
            return;
        }
        await searchLogger.log({
            query,
            requestType: 'suggest',
            redirectUrl: url,
            asPath,
            label,
            suggestsCount: suggests.length,
            pasted,
        });
        const resultUrl = addParams(url, query);
        setPasted([]);
        window.open(resultUrl, '_self');
        pushEvent({
            event: 'mainEvent',
            eventCategory: 'Поиск',
            eventAction: 'Отправка запроса|Тип выдачи - Авторедирект',
            eventLabel: `Запрос - ${query}|Способ ввода - Подсказка`,
        });
    }, [searchLogger, query, asPath, suggests.length, pasted, setIsScenarioMode, searchServiceHost, pushEvent]);
    const handleClassicalResultClick = useCallback((url) => {
        const resultUrl = addParams(url, query, true);
        window.open(resultUrl);
    }, [query]);
    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const focusedSuggest = suggests[focusedSuggestIndex];
            if (focusedSuggest) {
                handleSuggestClick(focusedSuggest.url, focusedSuggest.searchRequest);
            }
            else {
                handleClick();
            }
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedSuggestIndex((prevIndex) => (prevIndex + 1) % suggests.length);
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedSuggestIndex((prevIndex) => prevIndex === 0 ? suggests.length - 1 : (prevIndex - 1) % suggests.length);
        }
    };
    const onReset = useCallback(() => {
        if (isResultsMode) {
            setResultQuery('');
        }
        else {
            setQuery('');
        }
    }, [isResultsMode]);
    const getInputIcon = useCallback(() => {
        if (isResultsMode) {
            if (!resultQuery) {
                return React.createElement(Icon, { icon: React.createElement(TailArrowRight, null), className: cx(modules_1ba53a2f.arrowIcon) });
            }
            return React.createElement(Icon, { icon: React.createElement(Cross, null), onClick: onReset, className: cx(modules_1ba53a2f.crossIcon) });
        }
        if (query) {
            return React.createElement(Icon, { icon: React.createElement(Cross, null), onClick: onReset, className: cx(modules_1ba53a2f.crossIcon) });
        }
        return null;
    }, [isResultsMode, resultQuery, query, cx, onReset]);
    const resultModeContent = () => (React.createElement("div", { className: cx(modules_1ba53a2f.resultsContent, {
            [modules_1ba53a2f.resultsContentMoved]: isResultsFocused,
        }) },
        React.createElement("div", { className: cx(modules_1ba53a2f.resultsTitle) },
            React.createElement("b", null, "\u0420\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442\u044B \u043F\u043E\u0438\u0441\u043A\u0430"),
            " ",
            React.createElement("span", { className: cx(modules_1ba53a2f.resultsQuery) },
                "\u201C",
                resultsTitle,
                "\u201D")),
        resultItems?.map((item) => (React.createElement("div", { className: cx(modules_1ba53a2f.resultItem), key: item.link },
            React.createElement("div", { onClick: () => handleClassicalResultClick(item.link), className: cx(modules_1ba53a2f.resultTitle) }, item.title),
            React.createElement("div", { className: cx(modules_1ba53a2f.resultDescription) }, item.snippet)))),
        resultItems.length <= 0 && !isResultsLoading ? (React.createElement("p", null,
            "\u041F\u043E \u0437\u0430\u043F\u0440\u043E\u0441\u0443 ",
            React.createElement("span", { className: cx(modules_1ba53a2f.resultsQuery) },
                "\"",
                resultsTitle,
                "\""),
            " \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E")) : null,
        isResultsLoading && (React.createElement(Skeleton, { className: modules_1ba53a2f.skeleton },
            React.createElement("div", null,
                React.createElement(Skeleton.Paragraph, { className: modules_1ba53a2f.title }),
                React.createElement(Skeleton.Paragraph, { className: modules_1ba53a2f.title, width: 50 })),
            React.createElement("div", { style: { width: '96%' } },
                React.createElement(Skeleton.Paragraph, { className: modules_1ba53a2f.subTitle }),
                React.createElement("div", { style: { width: '90%' } },
                    React.createElement(Skeleton.Paragraph, { className: modules_1ba53a2f.subTitle }))))),
        resultItems.length > 0 && !isResultsLoading ? (React.createElement("div", { onClick: loadResults, className: cx(modules_1ba53a2f.moreLabel) }, "\u041F\u041E\u041A\u0410\u0417\u0410\u0422\u042C \u0415\u0429\u0401")) : null));
    const getDialogMainContent = () => {
        if (status === SearchSuggestsStatus.Error) {
            return React.createElement(ErrorResponse, { onClick: () => setStatus(SearchSuggestsStatus.Default) });
        }
        if (isScenarioMode && scenarioData) {
            return (React.createElement(Scenario, { searchServiceHost: searchServiceHost, query: scenarioQuery, queryInput: query, data: scenarioData, closeScenario: setIsScenarioMode.off, startMessages: resultsTitle
                    ? [
                        createMessage(React.createElement(GoogleResultList, { query: resultsTitle, searchServiceHost: searchServiceHost }), MESSAGE_TYPE.COMPONENT, false),
                    ]
                    : [], startNewScenario: (label, name) => handleSuggestClick('', label, name) }));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Dialog.Content, { className: cx(modules_1ba53a2f.content, modules_1ba53a2f.contentSpace, isResultsMode && modules_1ba53a2f.resultContentSpace) },
                isMobile && !isResultsMode && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, { [modules_1ba53a2f.notEmpty]: query, [modules_1ba53a2f['flex-end']]: query }) },
                    !query && React.createElement(Text, { className: modules_1ba53a2f.menuTitle }, "\u0427\u0410\u0421\u0422\u041E \u0418\u0429\u0423\u0422"),
                    React.createElement(SuggestList, { suggests: suggests, focusedIndex: focusedSuggestIndex, onClick: handleSuggestClick }))),
                isResultsMode && (React.createElement("div", { className: cx(modules_1ba53a2f.contentSpaceInner) },
                    React.createElement("div", { className: cx(modules_1ba53a2f.contentSpaceInnerContent, {
                            [modules_1ba53a2f.contentSpaceInnerContentMoved]: isResultsFocused,
                            [modules_1ba53a2f.contentSpaceInnerContentWithValue]: query,
                        }) },
                        React.createElement("div", { className: cx(modules_1ba53a2f.contentMainContainer, { [modules_1ba53a2f.m_t_10]: !isMobile }) },
                            React.createElement("div", { ref: contentMainRef, className: cx(modules_1ba53a2f.contentMain) }, resultModeContent()),
                            isMobile && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, { [modules_1ba53a2f.notEmpty]: query }) },
                                React.createElement(SuggestList, { suggests: suggests, focusedIndex: focusedSuggestIndex, onClick: handleSuggestClick })))),
                        !isMobile && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, { [modules_1ba53a2f.notEmpty]: query }) },
                            React.createElement(SuggestList, { suggests: suggests, focusedIndex: focusedSuggestIndex, onClick: handleSuggestClick }))))))),
            React.createElement(Dialog.Footer, { className: cx(modules_1ba53a2f.footer, { [modules_1ba53a2f['p-0']]: !isMobile && !isResultsMode }) },
                isMobile && isResultsMode && isResultsFocused && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, modules_1ba53a2f.menuDesktop, { [modules_1ba53a2f.notEmpty]: resultQuery }) },
                    React.createElement(SuggestList, { suggests: resultSuggests, onClick: handleSuggestClick }))),
                React.createElement(TextInput, { loading: status === SearchSuggestsStatus.Loading, onKeyDown: onKeyDown, ref: inputRef, placeholder: isResultsMode ? 'Найти еще что то' : 'Искать на Сравни', value: isResultsMode ? resultQuery : query, onChange: handleChange, icon: getInputIcon(), "aria-selected": true, autoFocus: true, onPaste: handlePaste }),
                !isMobile && isResultsMode && isResultsFocused && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, modules_1ba53a2f.menuDesktop, { [modules_1ba53a2f.notEmpty]: resultQuery }) },
                    React.createElement(SuggestList, { suggests: resultSuggests, onClick: handleSuggestClick }))),
                !isMobile && !isResultsMode && (React.createElement("div", { className: cx(modules_1ba53a2f.menu, { [modules_1ba53a2f.notEmpty]: query }) },
                    !query && React.createElement(Text, { className: modules_1ba53a2f.menuTitle }, "\u0427\u0410\u0421\u0422\u041E \u0418\u0429\u0423\u0422"),
                    React.createElement(SuggestList, { suggests: suggests, focusedIndex: focusedSuggestIndex, onClick: handleSuggestClick }))))));
    };
    return (React.createElement(Dialog, { className: cx('searchDialog', isResultsMode && 'resultsSearchDialog', isScenarioMode && 'scenarioDialog'), visible: isVisible, onClose: onClose, closable: false, fullscreen: isMobile, ref: dialogContentRef },
        (isResultsMode || isMobile || isScenarioMode) && (React.createElement("div", { className: cx('dialogHeader', isResultsMode && 'resultDialogHeader') },
            React.createElement(Text, { className: modules_1ba53a2f.headerTitle }, "\u041F\u043E\u0438\u0441\u043A"),
            React.createElement(Icon, { icon: React.createElement(Cross, null), onClick: onClose, className: modules_1ba53a2f.closeIcon }))),
        getDialogMainContent()));
};

export { SearchDialog };
//# sourceMappingURL=Dialog.js.map
